import React, { Component } from "react";
import clsx from "clsx";

import BoModal from "../common/BoModal";
import BackofficeService from "../../../services/BackofficeService";
import { I } from "../common/icons";
import { SERVICES, FREQUENCIES, ROLE_ORDER, ROLES } from "../backofficeConstants";
import { fmt, hrs, periodOptions, periodFor, today, userShort } from "../backofficeUtils";

/**
 * Prepopulate a client's service from a template for one period. Shows the exact tasks that
 * will be added (dates, repeats, budget, people) and lets individual rows be left out.
 */
class TemplateModal extends Component {
	constructor(props) {
		super(props);
		const { templates, initialClientId, initialService, initialPeriodStart, clients, myUserId } = props;
		const tpl = templates.find(t => initialService && t.service === initialService) || templates[0] || null;
		const service = tpl ? tpl.service : "monthend";
		this.state = {
			clientId: initialClientId || (clients[0] ? clients[0].clientId : null),
			templateId: tpl ? tpl.id : null,
			periodStart: initialPeriodStart ? periodFor(service, initialPeriodStart).start : this.defaultPeriod(service),
			preparerUserId: myUserId || "",
			reviewerUserId: "",
			managerUserId: "",
			skipDuplicates: true,
			activateEmails: true,
			excluded: new Set(),
			preview: null,
			loading: false,
			applying: false,
			error: null,
		};
	}

	componentDidMount() {
		this.loadPreview();
	}

	defaultPeriod(service) {
		const p = periodFor(service, today());
		return service === "monthend" ? periodOptions(service, 2)[0].start : p.start;
	}

	template() {
		return this.props.templates.find(t => t.id === this.state.templateId) || null;
	}

	loadPreview = async () => {
		const { templateId, clientId, periodStart } = this.state;
		if (!templateId) return;
		this.setState({ loading: true, error: null });
		const result = await BackofficeService.previewTemplate({ templateId, clientId, periodStart });
		if (!result.ok) return this.setState({ loading: false, error: result.error, preview: null });
		const excluded = new Set(this.state.skipDuplicates ? result.data.tasks.filter(t => t.duplicate).map(t => t.id) : []);
		this.setState({ loading: false, preview: result.data, excluded });
	};

	pickTemplate = tpl => {
		const periodChanged = tpl.service !== (this.template() || {}).service;
		this.setState({ templateId: tpl.id, periodStart: periodChanged ? this.defaultPeriod(tpl.service) : this.state.periodStart }, this.loadPreview);
	};

	setAndReload = (key, value) => this.setState({ [key]: value }, this.loadPreview);

	toggleRow = (id, include) => {
		const excluded = new Set(this.state.excluded);
		include ? excluded.delete(id) : excluded.add(id);
		this.setState({ excluded });
	};

	apply = async () => {
		const s = this.state;
		const tpl = this.template();
		if (!tpl || !s.clientId) return;
		this.setState({ applying: true, error: null });
		const include = s.preview ? s.preview.tasks.filter(t => !s.excluded.has(t.id)).map(t => t.id) : undefined;
		const result = await BackofficeService.applyTemplate({
			templateId: tpl.id,
			clientId: parseInt(s.clientId, 10),
			periodStart: s.periodStart,
			preparerUserId: s.preparerUserId ? parseInt(s.preparerUserId, 10) : null,
			reviewerUserId: s.reviewerUserId ? parseInt(s.reviewerUserId, 10) : null,
			managerUserId: s.managerUserId ? parseInt(s.managerUserId, 10) : null,
			skipDuplicates: s.skipDuplicates,
			activateEmails: s.activateEmails,
			includeTemplateTaskIds: include,
		});
		this.setState({ applying: false });
		if (!result.ok) return this.setState({ error: result.error });
		this.props.onApplied({ added: result.data.added, period: result.data.period, tasks: result.data.tasks, service: tpl.service, clientId: parseInt(s.clientId, 10) });
	};

	renderRoleSelect(roleKey) {
		const role = ROLES[roleKey];
		const field = role.idField;
		return (
			<div className="bo-field" key={roleKey}>
				<label>
					<span className={clsx("bo-role", `bo-role--${role.cls}`)}>{role.short}</span>
					Default {role.label.toLowerCase()}
				</label>
				<select value={this.state[field] || ""} onChange={e => this.setState({ [field]: e.target.value })}>
					<option value="">Unassigned</option>
					{this.props.users.map(u => (
						<option key={u.id} value={u.id}>
							{u.name}
							{u.title ? ` (${u.title})` : ""}
						</option>
					))}
				</select>
			</div>
		);
	}

	render() {
		const { clients, templates, users, onClose } = this.props;
		const s = this.state;
		const tpl = this.template();
		const rows = s.preview ? s.preview.tasks : [];
		const included = rows.filter(r => !s.excluded.has(r.id));
		const periods = tpl ? periodOptions(tpl.service, 8) : [];
		if (tpl && !periods.some(p => p.start === s.periodStart)) periods.unshift({ start: s.periodStart, label: s.preview ? s.preview.period.label : s.periodStart });
		const roles = { preparer: s.preparerUserId, reviewer: s.reviewerUserId, manager: s.managerUserId };

		return (
			<BoModal
				title="Prepopulate tasks from template"
				icon={<I.file />}
				wide
				onClose={onClose}
				footerLeft={
					<>
						<I.info />
						Templates are managed under Settings › BO Templates.
					</>
				}
				footer={
					<>
						<button className="bo-btn" type="button" onClick={onClose}>
							Cancel
						</button>
						<button className="bo-btn bo-btn--primary ft-bo-template-apply" type="button" onClick={this.apply} disabled={s.applying || s.loading || !tpl || !s.clientId}>
							{s.applying ? "Adding…" : included.length ? `Add ${included.length} task${included.length === 1 ? "" : "s"}` : "Create empty service"}
						</button>
					</>
				}
			>
				{s.error && <div className="bo-field__error">{s.error}</div>}
				<div className="bo-grid2">
					<div className="bo-field">
						<label htmlFor="tplClient">Client</label>
						<select id="tplClient" value={s.clientId || ""} onChange={e => this.setAndReload("clientId", parseInt(e.target.value, 10))}>
							{clients.map(c => (
								<option key={c.id} value={c.clientId}>
									{c.name}
								</option>
							))}
						</select>
					</div>
					<div className="bo-field">
						<label htmlFor="tplPeriod">
							Period <small>(anchors all dates)</small>
						</label>
						<select id="tplPeriod" value={s.periodStart} onChange={e => this.setAndReload("periodStart", e.target.value)}>
							{periods.map(p => (
								<option key={p.start} value={p.start}>
									{p.label}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="bo-field">
					<label>Template</label>
					<div className="bo-tpl-list">
						{templates.map(t => (
							<button key={t.id} type="button" className={clsx("bo-tpl", { "bo-tpl--on": t.id === s.templateId })} onClick={() => this.pickTemplate(t)}>
								<span className="bo-tpl__sw" style={{ background: SERVICES[t.service].color }} />
								<span>
									<b>{t.name}</b>
									<span>{t.description || `${t.tasks.length} task${t.tasks.length === 1 ? "" : "s"}`}</span>
								</span>
							</button>
						))}
						{!templates.length && <div className="bo-muted">No templates yet. Create one under Settings › BO Templates.</div>}
					</div>
				</div>
				<div className="bo-roleset">{ROLE_ORDER.map(r => this.renderRoleSelect(r))}</div>
				<div className="bo-field">
					<label>
						Tasks to add <small>{s.loading ? "(loading…)" : included.length ? `(${included.length} selected)` : "(none)"}</small>
					</label>
					<div className="bo-preview-wrap">
						<div className="bo-preview">
							{rows.length > 0 && (
								<div className="bo-preview__ph">
									<span />
									<span>Task</span>
									<span>Schedule</span>
									<span>Repeats</span>
									<span>Budget</span>
									<span>Roles</span>
								</div>
							)}
							{rows.map(r => (
								<div key={r.id} className={clsx("bo-preview__pr", { "bo-preview__pr--dup": r.duplicate })}>
									<input type="checkbox" checked={!s.excluded.has(r.id)} onChange={e => this.toggleRow(r.id, e.target.checked)} aria-label="Include" />
									<span>
										{r.title}
										{r.groupName && <span className="bo-preview__sub"> · {r.groupName}</span>}
										{r.email && <span className="bo-pill bo-pill--amber bo-pill--xs"> Email</span>}
										{r.accounts.length > 0 && <span className="bo-pill bo-pill--green bo-pill--xs"> {r.accounts.length} accounts</span>}
										{r.duplicate && <span className="bo-pill bo-pill--grey bo-pill--xs"> already exists</span>}
									</span>
									<span className="bo-mono">
										{fmt(r.startDate)} → {fmt(r.dueDate)}
									</span>
									<span className="bo-preview__sub">{FREQUENCIES[r.frequency]}</span>
									<span className="bo-mono">{hrs(r.budget.preparer + r.budget.reviewer + r.budget.manager)}h</span>
									<span className="bo-roles">
										{ROLE_ORDER.map(k => (
											<span key={k} className={clsx("bo-role", `bo-role--${ROLES[k].cls}`)}>
												{userShort(users, parseInt(roles[k], 10))}
											</span>
										))}
									</span>
								</div>
							))}
							{!s.loading && !rows.length && <div className="bo-preview__pr bo-preview__single">This template is empty. You can add tasks one at a time after applying it.</div>}
						</div>
					</div>
				</div>
				<label className="bo-chk">
					<input type="checkbox" checked={s.skipDuplicates} onChange={e => this.setState({ skipDuplicates: e.target.checked }, this.loadPreview)} /> Skip tasks that already exist for this client and period
				</label>
				<label className="bo-chk">
					<input type="checkbox" checked={s.activateEmails} onChange={e => this.setState({ activateEmails: e.target.checked })} /> Activate client email automations included in the template
				</label>
			</BoModal>
		);
	}
}

export default TemplateModal;
